<template>
  <div
    v-if="isLoading"
    class="d-flex flex-column align-center justify-center"
    style="height: 100%;"
  >
    <v-progress-circular
      indeterminate
      color="primary"
      size="64"
    />
  </div>
  <div
    v-else
    class="d-flex flex-column"
    style="height: 100%;"
  >
    <v-toolbar
      class="shrink"
      flat
    >
      <div class="mr-2">
        <v-tooltip top>
          <template #activator="{ on, attrs }">
            <v-btn
              :disabled="isLoading"
              icon
              v-bind="attrs"
              v-on="on"
              @click="_ => goToPreviousCalendarScreen()"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
          </template>
          <span>Go to previous {{ selectedCalendarView.title }}</span>
        </v-tooltip>
        <v-tooltip top>
          <template #activator="{ on, attrs }">
            <v-btn
              :disabled="isLoading"
              icon
              v-bind="attrs"
              v-on="on"
              @click="_ => goToToday()"
            >
              <v-icon>mdi-calendar-today</v-icon>
            </v-btn>
          </template>
          <span>Go to Today</span>
        </v-tooltip>
        <v-tooltip top>
          <template #activator="{ on, attrs }">
            <v-btn
              :disabled="isLoading"
              icon
              v-bind="attrs"
              v-on="on"
              @click="_ => goToNextCalendarScreen()"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </template>
          <span>Go to next {{ selectedCalendarView.title }}</span>
        </v-tooltip>
      </div>

      <v-menu
        v-model="showDateSelectionDatePicker"
        bottom
        :close-on-content-click="false"
        min-width="auto"
        offset-y
        right
        transition="scale-transition"
      >
        <template #activator="{ on, attrs }">
          <v-toolbar-title
            v-bind="attrs"
            v-on="on"
          >
            {{ dateDisplayFormat(calendarDate) }}
          </v-toolbar-title>
        </template>
        <v-date-picker
          :disabled="isLoading"
          :events="hasEventsForDate"
          :value="calendarDate"
          @input="newDate => changeCalendarDate(newDate)"
        />
      </v-menu>

      <v-spacer />

      <div class="ml-2">
        <v-tooltip
          bottom
        >
          <template #activator="{ attrs, on }">
            <v-btn
              :disabled="isLoading"
              height="48"
              outlined
              text
              v-bind="attrs"
              v-on="on"
              @click="_ => toggleLinkedCalendarsEvents()"
            >
              <v-badge
                bordered
                class="small-badge"
                :color="linkedCalendarsEventsVisibilityIconColor"
                :icon="linkedCalendarsEventsVisibilityIcon"
                offset-x="10"
                offset-y="10"
                overlay
                right
                top
              >
                <v-icon>
                  mdi-calendar-badge
                </v-icon>
              </v-badge>
            </v-btn>
          </template>
          <span>Show/Hide Linked Calendars Events</span>
        </v-tooltip>
      </div>

      <div class="ml-2">
        <UserSelect
          v-model="selectedUsers"
          :allowed-users="allowedUsers"
          hide-disabled
          :loading="isLoading"
          mode="menu"
          only-assignable
          :open="showUserSelectionMenu"
        />
      </div>

      <div class="ml-2">
        <v-btn-toggle
          v-model="calendarView"
        >
          <v-tooltip
            v-for="view of availableCalendarViews"
            :key="view.value"
            bottom
          >
            <template #activator="{ on, attrs }">
              <v-btn
                :disabled="isLoading"
                :value="view.value"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>
                  {{ view.icon }}
                </v-icon>
              </v-btn>
            </template>
            <span>Show for {{ view.title }}</span>
          </v-tooltip>
        </v-btn-toggle>
      </div>
    </v-toolbar>

    <v-card-text
      class="grow px-4 pb-4"
    >
      <div style="position: relative; height: 100%;">
        <FullCalendar
          ref="fullCalendar"
          class="availability-calendar"
          :style="{ opacity: requiredFieldsSet ? 1 : 0 }"
          :options="fullCalendarOptions"
        >
          <template #eventContent="event">
            <CalendarEvent
              :calendar-event="event"
              @click="e => onAvailabilitySlotSelected(e.id)"
            />
          </template>
        </FullCalendar>
        <div
          v-if="isLoading"
          class="d-flex align-center justify-center"
          style="position: absolute; width: 100%; height: 100%; top: 0; left: 0; background-color: rgba(255, 255, 255, .5); z-index: 1;"
        >
          <v-progress-circular
            color="primary"
            indeterminate
            size="64"
          />
        </div>
        <div
          v-else-if="!requiredFieldsSet"
          class="d-flex align-center justify-center amber lighten-5 rounded"
          style="position: absolute; width: 100%; height: 100%; top: 0; left: 0;"
        >
          <span>Please select, at least, one assignee.</span>
        </div>
      </div>
    </v-card-text>

    <v-card-text
      v-if="selectedUsers.length"
      class="shrink px-4 pb-2"
    >
      <v-chip
        v-for="selectedUserId of selectedUsers"
        :key="selectedUserId"
        class="mr-2 mb-2"
        :color="userColors[selectedUserId]"
        :disabled="isLoading"
        label
        dark
      >
        {{ getUserNameByUserId(selectedUserId) }}
      </v-chip>
    </v-card-text>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import moment from 'moment-timezone'
import momentTimezonePlugin from '@fullcalendar/moment-timezone'
import resourceTimelinePlugin from '@fullcalendar/resource-timeline'
import timeGridPlugin from '@fullcalendar/timegrid'

import { alertHandler } from '@/alertHandler'
import { dateDisplayFormat } from '@/utilities'

import { CALENDAR_NAMESPACE } from '@/store/calendar'
import { DOCKET_ENTRY_NAMESPACE } from '@/store/docket-entry'
import { DOCKET_ENTRY_TYPE_NAMESPACE } from '@/store/docket-entry-type'
import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'

import CalendarEvent from '@/components/Calendar/Calendar/CalendarEvent.vue'
import FullCalendar from '@fullcalendar/vue'
import UserSelect from '@/components/Fields/UserSelect.vue'

const visuallyDifferentiableColors = [
  '#4a463f', '#e6d3a5', '#c7a842', '#b9b9b9', '#7de8ff', '#0063b0', '#005bcd',
  '#8791a2', '#625e56', '#cfb778', '#9a8232', '#ababab', '#7cdaff', '#87cbff',
  '#6c93c5', '#43474e', '#7b776e', '#4f462f', '#f7d153', '#848484', '#41bfff',
  '#0071d8', '#5a79a1', '#afb9cb', '#bdb8af', '#a08f65', '#a18100', '#777777',
  '#00a8ff', '#004b8d', '#38485e', '#d9e3f5', '#e7e2d9', '#887543', '#564500',
  '#5e5e5e', '#0362a0', '#0059b3', '#505f76', '#5a5f66', '#b6aa90', '#f0d27e',
  '#bd9b00', '#474747', '#1288da', '#b1d7ff', '#687890', '#73777f', '#7e7666',
  '#52461f', '#ebc30e', '#00ebff', '#004a7d', '#0075fb', '#96adcf', '#e0d3b8',
  '#775c00', '#ffde00', '#00ddff', '#bfe5ff', '#33547a', '#0061f8', '#675d46',
  '#6f5d23', '#d4d4d4', '#00d0ff', '#008af5', '#83bcff', '#495362',
]

export default {
  name: 'DocketEntryEditorAvailability',

  components: {
    CalendarEvent,
    FullCalendar,
    UserSelect,
  },

  props: {
    availabilitySlotId: {
      type: Number,
      default: null,
    },

    clickable: {
      default: true,
      type: Boolean,
    },

    customOptions: {
      default: _ => ({}),
      type: Object,
    },

    defaultCalendarView: {
      type: String,
      default: null,
    },

    enabledViews: {
      type: Array,
      default: null,
    },

    filterByType: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      allowedUsers: [],
      areLinkedCalendarsEventsVisible: true,
      availabilitySlots: [],
      calendarDate: moment().format('YYYY-MM-DD'),
      calendarItems: [],
      calendarView: this.defaultCalendarView || 'day',
      calendarViews: [
        {
          icon: 'mdi-calendar',
          plugin: 'timeGridDay',
          title: 'Day',
          value: 'day',
        },
        {
          icon: 'mdi-calendar-week',
          plugin: 'timeGridWeek',
          title: 'Week',
          value: 'week',
        },
        {
          icon: 'mdi-calendar-month',
          plugin: 'dayGridMonth',
          title: 'Month',
          value: 'month',
        },
      ],

      defaultTimezone: '',
      isLoading: false,
      selectedAvailabilitySlotId: null,
      selectedUsers: [],
      showDateSelectionDatePicker: false,
      showTimezoneSelectionMenu: false,
      showUserSelectionMenu: false,
      userColors: {},
    }
  },

  computed: {
    adHocAllowedUsers () {
      return this.adHocUsers.reduce((res, userId) => {
        if (this.allowedUsers.includes(userId)) res.push(userId)
        return res
      }, [])
    },

    adHocUsers () {
      return [
        this.currentUser.id,
        ...this.receivedPermissionsFor(this.currentUser.id, 'CreateAdHocAppointments').map(gp => gp.fromUserId),
      ]
    },

    assignees () {
      return this.docketEntry.docket_entries_assignments.
        reduce((res, dea) => {
          if (!dea._destroy) res.push(dea.user_id)
          return res
        }, [])
    },

    availableCalendarViews () {
      if (this.enabledViews?.length > 0) {
        return this.calendarViews.filter(view => this.enabledViews.includes(view.value))
      } else {
        return this.calendarViews
      }
    },

    docketEntry () {
      return this.editedDocketEntry
    },

    docketEntryTypeId () {
      return this.docketEntry.docket_entry_type_id
    },

    filteredCalendarItems () {
      return this.availabilitySlots.reduce((res, s) => {
        if (!this.selectedUsers.includes(s.userId)) return res

        const det = this.docketEntryTypes.find(det => det.id === s.docketEntryTypeId)

        if (!det || !(
          det.all_legal_case_types || det.legal_case_type_ids.includes(this.legalCase.legal_case_type_id)
        )) {
          return res
        }

        res.push({
          backgroundColor: this.userColors[s.userId],
          borderColor: this.userColors[s.userId],
          classNames: [
            s.id === this.selectedAvailabilitySlotId ? 'selected-calendar-event' : '',
          ],
          display: 'block',
          end: s.endDate,
          id: s.id,
          resourceId: s.userId,
          start: s.startDate,
          title: s.title,
        })

        return res
      }, []).
        concat((this.areLinkedCalendarsEventsVisible ? this.calendarItems : []).reduce((res, ci) => {
          if (this.selectedUsers.includes(ci.userId)) {
            res.push({
              allDay: ci.allDay,
              backgroundColor: this.userColors[ci.userId],
              borderColor: this.userColors[ci.userId],
              display: 'block',
              end: ci.endDate,
              extendedProps: {
                id: ci.id,
                service: ci.service,
              },
              id: ci.id,
              resourceId: ci.userId,
              start: ci.startDate,
              title: ci.title,
            })
          }

          return res
        }, []))
    },

    fullCalendarOptions () {
      return Object.assign({
        editable: false,
        events: this.refreshCalendarEvents,
        headerToolbar: false,
        height: '100%',
        initialDate: this.calendarDate,
        initialView: this.selectedCalendarView.plugin,
        nowIndicator: false,

        plugins: [
          dayGridPlugin,
          interactionPlugin,
          momentTimezonePlugin,
          resourceTimelinePlugin,
          timeGridPlugin,
        ],

        resourceAreaColumns: [
          {
            field: 'name',
            headerContent: 'User',
          },
        ],

        resources: this.refreshCalendarResources,
        schedulerLicenseKey: 'CC-Attribution-NonCommercial-NoDerivatives',
        select: this.onCalendarDateSelected,
        selectable: true,
        slotDuration: '00:30:00',
        timeZone: this.docketEntry.timezone,
      }, this.customOptions)
    },

    linkedCalendarsEventsVisibilityIcon () {
      return this.areLinkedCalendarsEventsVisible ? 'mdi-check' : 'mdi-close'
    },

    linkedCalendarsEventsVisibilityIconColor () {
      return this.areLinkedCalendarsEventsVisible ? 'green' : 'red'
    },

    requiredFieldsSet () {
      return this.assignees.length
    },

    selectedCalendarView () {
      return this.availableCalendarViews.find(cv => cv.value === this.calendarView) ||
        this.availableCalendarViews.find(cv => cv.value === (this.defaultCalendarView || 'day'))
    },

    timezone () {
      return this.docketEntry.timezone
    },

    usersThatNeedSlots () {
      let startDate = moment.tz(
        `${this.docketEntry.action_date} ${this.docketEntry.event_time_start}`,
        'YYYY-MM-DD hh:mm a',
        this.docketEntry.timezone
      )

      let endDate = moment.tz(
        `${this.docketEntry.action_date} ${this.docketEntry.event_time_end}`,
        'YYYY-MM-DD hh:mm a',
        this.docketEntry.timezone
      )

      if (!startDate.isValid() || !endDate.isValid()) return []

      startDate = startDate.utc()
      endDate = endDate.utc()

      const usersWithSlots = this.availabilitySlots.reduce((res, slot) => {
        if (slot.docketEntryTypeId === this.docketEntry.docket_entry_type_id &&
            moment(slot.startDate).utc().isSame(startDate) &&
            moment(slot.endDate).utc().isSame(endDate)
        ) {
          if (!res.includes(slot.userId)) res.push(slot.userId)
        }

        return res
      }, [])

      return this.allowedUsers.reduce((res, userId) => {
        if (!this.adHocAllowedUsers.includes(userId) && !usersWithSlots.includes(userId)) res.push(userId)
        return res
      }, [])
    },

    ...mapGetters([
      'currentUser',
      'usersOptions',
    ]),

    ...mapGetters(CALENDAR_NAMESPACE, [
      'receivedPermissionsFor',
    ]),

    ...mapGetters(DOCKET_ENTRY_NAMESPACE, [
      'editedDocketEntry',
    ]),

    ...mapGetters(DOCKET_ENTRY_TYPE_NAMESPACE, [
      'docketEntryTypes',
    ]),

    ...mapGetters(LEGAL_CASE_NAMESPACE, [
      'legalCase',
    ]),
  },

  watch: {
    assignees: {
      handler (newVal, oldVal) {
        const addedAssignees = newVal?.filter(uid => !oldVal?.includes(uid)) || []
        addedAssignees.forEach(uid => this.addAllowedUser(uid))

        const removedAssignees = oldVal?.filter(uid => !newVal?.includes(uid)) || []
        removedAssignees.forEach(uid => this.removeAllowedUser(uid))

        if (addedAssignees.length) {
          this.reloadCalendarEvents()
        }
      },

      immediate: true,
    },

    availabilitySlotId: {
      handler (val) { this.selectedAvailabilitySlotId = val },
      immediate: true,
    },

    calendarDate (newVal, oldVal) {
      if (!moment(newVal).isSame(moment(oldVal), 'month')) this.reloadCalendarEvents()
    },

    'docketEntry.action_date' (newVal, oldVal) {
      if (oldVal && newVal !== oldVal) this.selectedAvailabilitySlotId = null
      if (!newVal || (newVal || '').substring(0, 7) === this.calendarDate.substring(0, 7)) return

      const newDate = moment(newVal)
      if (!newDate.isValid()) return

      this.calendarDate = newDate.format('YYYY-MM-DD')
      this.reloadCalendarEvents()
    },

    'docketEntry.docket_entry_type_id' (newVal, oldVal) {
      if (oldVal && newVal !== oldVal) this.selectedAvailabilitySlotId = null
      if (!this.filterByType) return

      this.reloadCalendarEvents()
    },

    'docketEntry.event_datetime_end' (newVal, oldVal) {
      if (oldVal && newVal !== oldVal) this.selectedAvailabilitySlotId = null
    },

    'docketEntry.event_datetime_start' (newVal, oldVal) {
      if (oldVal && newVal !== oldVal) this.selectedAvailabilitySlotId = null
    },

    'docketEntry.event_time_end' (newVal, oldVal) {
      if (oldVal && newVal !== oldVal) this.selectedAvailabilitySlotId = null
    },

    'docketEntry.event_time_start' (newVal, oldVal) {
      if (oldVal && newVal !== oldVal) this.selectedAvailabilitySlotId = null
    },

    filteredCalendarItems () {
      if (!this.$refs.fullCalendar) return
      const calendar = this.$refs.fullCalendar.getApi()
      calendar.refetchEvents()
    },

    selectedAvailabilitySlotId (val) {
      this.$emit('update:availability-slot-id', val)
    },

    selectedCalendarView (value) {
      if (!value) return
      const calendar = this.$refs.fullCalendar?.getApi()
      if (!calendar) return
      calendar.changeView(value.plugin)
    },

    selectedUsers () {
      this.refreshCalendar()
    },

    timezone (newVal, oldVal) {
      if (newVal === oldVal) return
      this.selectedAvailabilitySlotId = null
      this.reloadCalendarEvents()
    },

    usersThatNeedSlots (val) {
      this.$emit('update:users-that-need-slots', val)
    },
  },

  async created () {
    this.isLoading = true

    this.resetPicker()

    await this.fetchAppointmentTypes()
    await this.fetchTimezones()
    await this.usersOptions()

    this.isLoading = false
  },

  methods: {
    addAllowedUser (userId) {
      if (this.allowedUsers.includes(userId)) return

      const colorsInUse = Object.values(this.userColors)
      const availableColors = visuallyDifferentiableColors.filter(c => !colorsInUse.includes(c))
      const cidx = Math.floor(Math.random() * availableColors.length)
      this.userColors[userId] = availableColors[cidx]

      this.allowedUsers.push(userId)
      this.selectedUsers.push(userId)
    },

    addToDate (date, quantity, interval) {
      return moment(date, 'YYYY-MM-DD').add(quantity, interval).format('YYYY-MM-DD')
    },

    changeCalendarDate (newDate) {
      this.showDateSelectionDatePicker = false
      this.calendarDate = typeof newDate === 'string' ? newDate : newDate.toISOString().substring(0, 10)

      const calendar = this.$refs.fullCalendar?.getApi()
      if (calendar) calendar.gotoDate(this.calendarDate)
    },

    dateDisplayFormat,

    getUserNameByUserId (uid) {
      const user = this.usersOptions().find(u => u.value === uid)
      return user ? `${user.displayName} (${user.login})` : 'Unknown'
    },

    goToNextCalendarScreen () {
      const newDate = this.addToDate(
        this.calendarDate,
        1,
        this.selectedCalendarView.value
      )

      this.changeCalendarDate(newDate)
    },

    goToPreviousCalendarScreen () {
      const newDate = this.addToDate(
        this.calendarDate,
        -1,
        this.selectedCalendarView.value
      )

      this.changeCalendarDate(newDate)
    },

    goToToday () {
      this.changeCalendarDate(new Date())
    },

    hasEventsForDate (date) {
      if (this.availabilitySlots.find(s => s.startDate.substring(0, 10) === date)) {
        return ['green']
      }

      return []
    },

    onAvailabilitySlotSelected (id) {
      if (this.clickable === false) return

      const selectedSlot = this.availabilitySlots.find(s => s.id.toString() === id)

      const startDate = moment(selectedSlot.startDate).tz(this.timezone)
      const endDate = moment(selectedSlot.endDate).tz(this.timezone)

      this.docketEntry.docket_entry_type_id = selectedSlot.docketEntryTypeId
      this.docketEntry.action_date = startDate.format('YYYY-MM-DD')
      this.docketEntry.event_time_end = endDate.format('hh:mm A')
      this.docketEntry.event_time_start = startDate.format('hh:mm A')
      this.docketEntry.event_datetime_start = startDate.format('YYYY-MM-DD hh:mm A')
      this.docketEntry.event_datetime_end = endDate.format('YYYY-MM-DD hh:mm A')

      this.$nextTick().then(_ => { this.selectedAvailabilitySlotId = selectedSlot.id })
    },

    onCalendarDateSelected (info) {
      const startDate = moment(info.start).tz(this.docketEntry.timezone)
      const endDate = moment(info.end).tz(this.docketEntry.timezone)

      this.docketEntry.action_date = startDate.format('YYYY-MM-DD')
      this.docketEntry.event_datetime_start = startDate.format('YYYY-MM-DD LT')
      this.docketEntry.event_datetime_end = endDate.format('YYYY-MM-DD LT')

      if (['timeGridDay', 'timeGridWeek'].includes(info.view.type)) {
        this.docketEntry.event_time_start = startDate.format('LT')
        this.docketEntry.event_time_end = endDate.format('LT')
      }
    },

    refreshCalendar () {
      const calendar = this.$refs.fullCalendar?.getApi()
      if (!calendar) return

      calendar.refetchResources()
      calendar.refetchEvents()
    },

    async refreshCalendarEvents () {
      return this.filteredCalendarItems
    },

    async refreshCalendarResources () {
      return this.$store.state.models.users.
        filter(u => this.selectedUsers.includes(u.id)).
        map(u => {
          return {
            id: u.id,
            name: `${u.first_name} ${u.last_name}`,
          }
        })
    },

    async reloadCalendarEvents () {
      this.availabilitySlots.splice(0)
      this.$emit('availability-slots-updated', [])

      if (!this.requiredFieldsSet) return

      this.isLoading = true

      if (this.selectedUsers.length > 0) {
        const startDate = moment(this.calendarDate).startOf('month').format()
        const endDate = moment(this.calendarDate).endOf('month').format()

        const slots = (this.filterByType)
          ? await this.fetchAvailabilitySlots({ docketEntryTypeId: this.docketEntryTypeId, userIds: this.selectedUsers })
          : await this.fetchAvailabilitySlots({ userIds: this.selectedUsers })

        this.availabilitySlots = (slots || [])

        // Emit slots to parent component for reactivity
        this.$emit('availability-slots-updated', this.availabilitySlots)

        this.calendarItems = await this.fetchLinkedCalendarsItems({
          endDate,
          startDate,
          userIds: this.selectedUsers,
        }) || []
      }

      this.refreshCalendar()
      this.isLoading = false
    },

    removeAllowedUser (userId) {
      const selectedIdx = this.selectedUsers.findIndex(au => au === userId)
      if (selectedIdx >= 0) this.selectedUsers.splice(selectedIdx, 1)

      if (this.userColors[userId]) delete this.userColors[userId]

      const allowedIdx = this.allowedUsers.findIndex(au => au === userId)
      if (allowedIdx >= 0) this.allowedUsers.splice(allowedIdx, 1)
    },

    resetPicker () {
      this.calendarDate = this.docketEntry?.action_date || (new Date()).toISOString().substring(0, 10)
      this.calendarView = this.defaultCalendarView || 'day'

      setTimeout(_ => { this.updateCalendarSize() }, 500)
    },

    toggleLinkedCalendarsEvents () {
      this.areLinkedCalendarsEventsVisible = !this.areLinkedCalendarsEventsVisible
    },

    updateCalendarSize () {
      const calendar = this.$refs.fullCalendar?.getApi()
      if (calendar) calendar.updateSize()
    },

    ...mapActions(CALENDAR_NAMESPACE, [
      'fetchAppointmentTypes',
      'fetchAvailabilitySlots',
      'fetchLinkedCalendarsItems',
      'fetchTimezones',
    ]),

  },
}
</script>

<style scoped>
.availability-calendar {
  .selected-calendar-event {
    border-color: white !important;
    outline: 4px solid var(--v-primary-base);
    outline-offset: 1px;
  }
}

::v-deep .fc-event-main-frame {
  .gcal-icon {
    right: 0;
    position: absolute;
  }
}

.small-badge {
  .v-badge__badge {
    min-width: auto;
    width: 14px;
    height: 14px;
    line-height: 14px;
    padding: 0;

    .v-icon {
      font-size: 9px;
    }
  }
}
</style>
